/**
 * Serverrenderade (prerenderade) sidtitlar.
 *
 * Varje ruttfil anropar `seoHead("/sokvag")` i sin `head()`, vilket gör att
 * <title>, og:title och twitter:title finns i HTML:en redan vid SSR/prerender.
 * Sidor som renderar PageHierarchySeo/CanonicalTag skriver över värdet i
 * webbläsaren (Helmet), men sökmotorer och länkförhandsvisningar ser rätt
 * titel direkt i serverns svar.
 */

import {
  HOME_TITLE,
  SITE_NAME,
  buildDescription,
  buildOfferTitle,
  buildTitle,
} from "./seoTitles";
import { SITE_URL, hierarchyLabel, normalizePath } from "./urlHierarchy";
import { templateTitle } from "./seoTitleTemplates";
import { getSeoOverride } from "./seoOverrideStore";
import { municipalities } from "@/data/municipalities";

/** Slug → kommunnamn, används för titlar på dynamiska kommunsidor. */
const MUNICIPALITY_NAMES = new Map(municipalities.map((m) => [m.slug, m.displayName || m.name]));

/** Sidor som saknar etikett i URL-hierarkin (inkl. dynamiska mallar). */
const FALLBACK_LABELS: Record<string, string> = {
  "/ai": "Elmarknad AI",
  "/ai-fakturaanalys": "AI-fakturaanalys",
  "/logga-in": "Logga in",
  "/diagram-tabeller": "Diagram & tabeller",
  "/elavtal/pensionarer": "Elavtal pensionär",
  "/elavtal/studenter": "Elavtal student",
  "/elpris-api": "Elpris API",
  "/elpriser/rorligt": "Rörligt elpris",
  "/for-llms": "För språkmodeller",
  "/forfragan-foretag-brf": "Förfrågan företag & BRF",
  "/hembatterier": "Hembatterier",
  "/historiska-elpriser/manad": "Historiska elpriser per månad",
  "/hitta-elnatsomrade": "Hitta elnätsområde",
  "/jamfor-offert": "Jämför offerter",
  "/jamfor-offerter": "Jämför offerter",
  "/offerter": "Jämför offerter",
  "/kassa": "Kassa",
  "/kommersiella-batterier": "Kommersiella batterier",
  "/kommersiella-solceller": "Kommersiella solceller",
  "/kommun": "Elpriser per kommun",
  "/mina-elpriser": "Mina elpriser",
  "/mitt-avtal": "Mitt avtal",
  "/mitt-elpris": "Mitt elpris",
  "/offertforfragan": "Offertförfrågan",
  "/personlig-energianalys": "Personlig energianalys",
  "/produkter": "Produkter",
  "/prognos-api": "Prognos-API för företag",
  "/sokresultat": "Sökresultat",
  "/spotpriser": "Spotpris per elområde",
  "/start": "Start",
  "/tack-foretag": "Tack för din förfrågan",
  "/tack-offert": "Tack för din förfrågan",
};

/** Offertsidor använder offertmallen: "Tjänst » Jämför 4 offerter | Elmarknad.se". */
const OFFER_SUBJECTS: Record<string, string> = {
  "/solceller/offert": "Solceller",
  "/batterier/offert": "Hembatterier",
  "/hembatterier/offert": "Hembatterier",
  "/laddboxar/offert": "Laddbox",
  "/laddstolpar/offert": "Laddstolpar",
  "/varmepumpar/offert": "Värmepump",
  "/kommersiella-solceller/offert": "Kommersiella solceller",
  "/kommersiella-batterier/offert": "Kommersiella batterier",
};

/** Löser titeln för en sökväg. Dynamiska segment ($slug) faller tillbaka på förälderns etikett. */
export function seoTitleForPath(path: string): string {
  let p = normalizePath(path);

  // Manuell överskrivning från adminpanelen vinner alltid.
  const override = getSeoOverride(p)?.title?.trim();
  if (override) return override;

  if (p === "/") return HOME_TITLE;

  const offer = OFFER_SUBJECTS[p];
  if (offer) return buildOfferTitle(offer);

  // Klipp bort dynamiska segment (t.ex. /blogg/$slug -> /blogg).
  while (p.includes("/$")) {
    p = p.slice(0, p.lastIndexOf("/")) || "/";
    if (p === "/") return HOME_TITLE;
  }

  const segments = p.split("/").filter(Boolean);
  const lastSlug = segments[segments.length - 1] ?? "";
  // Historiksidor: /elpriser/historik/elpriser-2023[/januari]
  const historyYear = /^elpriser-(\d{4})$/.exec(segments[2] ?? "")?.[1];
  if (historyYear) {
    const month = segments[3];
    return month
      ? `Elpriser ${month.charAt(0).toUpperCase()}${month.slice(1)} ${historyYear} » Snittpris på el per dag`
      : `Elpriser ${historyYear} » Snittpriser på el, månad för månad`;
  }
  const label =
    hierarchyLabel(p) ?? FALLBACK_LABELS[p] ?? MUNICIPALITY_NAMES.get(lastSlug);

  // Titelmall med » (t.ex. "Elpris idag per kWh » Dagens elpris på Nord Pool").
  const segment = p.split("/").filter(Boolean).pop() ?? "";
  const readable = segment.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  const templated = templateTitle(p, label ?? readable);
  if (templated) return templated;

  if (label) return buildTitle(label, "page");

  // Ingen etikett: härled ett läsbart namn från sista segmentet.
  return readable ? buildTitle(readable, "page") : HOME_TITLE;
}



/**
 * Beskrivningar per sektion (prefix-matchning, längsta träff vinner).
 * Används för og:description / twitter:description / meta description
 * redan i serverns svar, så länkförhandsvisningar blir sidspecifika.
 */
const SECTION_DESCRIPTIONS: Array<[string, string]> = [
  ["/elavtal/jamfor", "Jämför elavtal från alla Sveriges elbolag och hitta det billigaste avtalet för din adress och årsförbrukning."],
  ["/elavtal", "Jämför elavtal och elpriser – rörligt, fast och månadspris – och byt elbolag kostnadsfritt hos Elmarknad.se."],
  ["/elbolag", "Jämför elbolag och deras elavtal, priser och villkor. Se vilka elhandelsbolag som passar dig bäst."],
  ["/elpriser", "Se aktuella elpriser per elområde och kommun – dagens och morgondagens priser per timme och kvart."],
  ["/spotpris", "Se spotpriset på el timme för timme och kvart för kvart i SE1–SE4, både idag och imorgon."],
  ["/spotpriser", "Se spotpriset på el per elområde (SE1–SE4) med uppdaterade priser per timme och kvart."],
  ["/historiska-elpriser", "Historiska elpriser per dag, månad och år för elområdena SE1–SE4."],
  ["/solceller", "Jämför offerter på solceller från kvalitetssäkrade installatörer – kostnadsfritt och utan förpliktelser."],
  ["/kommersiella-solceller", "Jämför offerter på kommersiella solcellsanläggningar för företag, fastigheter och lantbruk."],
  ["/batterier", "Jämför offerter på hembatterier och batterilagring – kostnadsfritt och utan förpliktelser."],
  ["/hembatterier", "Jämför offerter på hembatterier och batterilagring – kostnadsfritt och utan förpliktelser."],
  ["/kommersiella-batterier", "Jämför offerter på kommersiell batterilagring för företag och fastigheter."],
  ["/varmepumpar", "Jämför offerter på värmepumpar – luft/vatten, frånluft och bergvärme – från kvalitetssäkrade installatörer."],
  ["/laddboxar", "Jämför offerter på laddbox till elbilen från kvalitetssäkrade installatörer."],
  ["/laddstolpar", "Jämför offerter på laddstolpar för företag, fastigheter och samfälligheter."],
  ["/elnat", "Läs om elnätet, nätavgifter och elnätsområden i Sverige."],
  ["/verktyg", "Kostnadsfria verktyg för dig som vill sänka din elkostnad – hitta elområde, nätägare och jämför elavtal."],
  ["/blogg", "Nyheter, guider och analyser om elmarknaden, elpriser och energi."],
  ["/produkter", "Utforska produkter inom solceller, batterilagring, värmepumpar och elbilsladdning – och jämför offerter."],
  ["/ai", "Ställ frågor om elpriser, elavtal och energi till Elmarknads AI-assistent."],
  ["/bli-partner", "Bli partner till Elmarknad.se och få kvalificerade leads inom el och energi."],
];

/**
 * Unika beskrivningar per exakt sökväg. Matchas före sektionsprefixen så att
 * undersidor (t.ex. /elavtal/byt och /elavtal/teckna) får egen text i både
 * meta description och Open Graph/Twitter – inte förälderns generiska.
 */
const PATH_DESCRIPTIONS: Record<string, string> = {
  // Elområdessidorna har var sin beskrivning (tidigare fick alla SE1:s text).
  "/elpriser/elomrade-1": "Elpriser i elområde 1 (SE1, Luleå) idag och imorgon. Norra Sverige har historiskt lägst elpriser tack vare stor vattenkraft och låg lokal efterfrågan.",
  "/elpriser/elomrade-2": "Elpriser i elområde 2 (SE2, Sundsvall) idag och imorgon. Mellersta Norrland har mycket vatten- och vindkraft och priser ofta i nivå med SE1.",
  "/elpriser/elomrade-3": "Elpriser i elområde 3 (SE3, Stockholm) idag och imorgon. Sveriges största elförbrukning – priset ligger oftast mellan norra Sverige och SE4.",
  "/elpriser/elomrade-4": "Elpriser i elområde 4 (SE4, Malmö) idag och imorgon. Södra Sverige har högst elpriser – hög efterfrågan, mindre lokal produktion och nära kontinenten.",
  "/spotpris/elomrade-se1": "Spotpriset i SE1 (Luleå) per timme och kvart, idag och imorgon från Nord Pool – med dygnets snittpris, lägsta och högsta pris i norra Norrland.",
  "/spotpris/elomrade-se2": "Spotpriset i SE2 (Sundsvall) per timme och kvart, idag och imorgon från Nord Pool – med dygnets snittpris, lägsta och högsta pris i mellersta Norrland.",
  "/spotpris/elomrade-se3": "Spotpriset i SE3 (Stockholm) per timme och kvart, idag och imorgon från Nord Pool – med dygnets snittpris, lägsta och högsta pris i Mellansverige.",
  "/spotpris/elomrade-se4": "Spotpriset i SE4 (Malmö) per timme och kvart, idag och imorgon från Nord Pool – med dygnets snittpris, lägsta och högsta pris i Sydsverige.",
  "/": "Jämför elpriser och hitta billigaste elavtalet – rörligt, fast, timpris eller mix. Se dagens elpriser per elområde och byt elbolag kostnadsfritt på några minuter.",

  // Elavtal
  "/elavtal": "Allt om elavtal: avtalstyper, priser, bindningstid och villkor – plus jämförelse av aktuella elavtal från Sveriges elbolag.",
  "/elavtal/jamfor": "Jämför elavtal från alla Sveriges elbolag för din adress och årsförbrukning – se totalpris per månad och teckna direkt.",
  "/elavtal/byt": "Byt elavtal på några minuter utan avbrott i elleveransen. Vi sköter uppsägningen av det gamla avtalet åt dig – helt kostnadsfritt.",
  "/elavtal/teckna": "Teckna nytt elavtal digitalt. Fyll i adress och förbrukning, välj avtal och signera – elen fortsätter utan avbrott.",
  "/elavtal/bestall": "Beställ elavtal direkt online. Se pris, bindningstid och villkor innan du bekräftar din beställning.",
  "/elavtal/sag-upp": "Så säger du upp ditt elavtal: uppsägningstid, bindningstid, brytavgift och vad som händer när avtalet löper ut.",
  "/elavtal/fast": "Fast elpris ger samma pris per kWh under hela bindningstiden. Se aktuella fastprisavtal och när de lönar sig.",
  "/elavtal/rorligt": "Rörligt elpris följer spotpriset månad för månad. Se aktuella rörliga avtal, påslag och när de lönar sig.",
  "/elavtal/timpris": "Timprisavtal följer elpriset timme för timme. Passar dig som kan flytta förbrukningen till billiga timmar.",
  "/elavtal/kvartspris": "Kvartsprisavtal följer elbörsens 15-minuterspriser. Se hur det skiljer sig från timpris och vem det passar.",
  "/elavtal/manadspris": "Rörligt månadspris ger ett snittpris för hela månaden – lugnare än timpris men följer ändå marknaden.",
  "/elavtal/mixpris": "Mixavtal delar förbrukningen mellan fast och rörligt pris. Se för- och nackdelar och vem det passar.",
  "/elavtal/portfolj": "Portföljavtal för företag och större förbrukare – så fungerar prissäkring i etapper och vad det kostar.",
  "/elavtal/tillsvidare": "Tillsvidarepris är elhandlarens dyraste avtal. Se vad det kostar och hur du snabbt byter till ett bättre.",
  "/elavtal/anvisat": "Anvisat elavtal får du automatiskt vid flytt – ofta till högt pris. Så byter du till ett eget avtal.",
  "/elavtal/binda": "Ska du binda elpriset? Se när fast pris lönar sig, vilka bindningstider som finns och vad det kostar att bryta.",
  "/elavtal/basta": "Bästa elavtalet just nu utifrån pris, villkor och avtalstyp – uppdaterade jämförelser för din adress.",
  "/elavtal/billiga": "Billiga elavtal med lågt påslag och låg månadsavgift – jämför de mest prisvärda avtalen för din förbrukning.",
  "/elavtal/villa": "Elavtal för villa med normalförbrukning 15 000–25 000 kWh per år – jämför avtal som passar villans förbrukning.",
  "/elavtal/lagenhet": "Elavtal för lägenhet med låg förbrukning – jämför avtal där låg månadsavgift betyder mest.",
  "/elavtal/fritidshus": "Elavtal för fritidshus och sommarstuga – avtal utan höga fasta avgifter under månader du inte är där.",
  "/elavtal/foretag": "Elavtal för företag – jämför fast, rörligt och portföljavtal och begär offert för företagets förbrukning.",
  "/elavtal/brf": "Elavtal för bostadsrättsföreningar – jämför avtal för fastighetsel och begär offert för föreningen.",
  "/elavtal/student": "Elavtal för studenter i studentlägenhet eller korridorrum – avtal med låg avgift och kort bindningstid.",
  "/elavtal/studenter": "Elavtal för studenter i studentlägenhet eller korridorrum – avtal med låg avgift och kort bindningstid.",
  "/elavtal/pensionar": "Elavtal för pensionärer – trygga avtal med förutsägbar kostnad och tydliga villkor.",
  "/elavtal/pensionarer": "Elavtal för pensionärer – trygga avtal med förutsägbar kostnad och tydliga villkor.",
  "/elavtal/forsta-gangen": "Teckna elavtal för första gången – så fungerar elnät, elhandel och avtalstyper steg för steg.",
  "/elavtal/vid-flytt": "Elavtal vid flytt: anmäl flyttdatum, undvik anvisat pris och ta med eller teckna nytt avtal i tid.",
  "/elavtal/avtalsvaljaren": "Svara på några frågor så föreslår avtalsväljaren den avtalstyp som passar din förbrukning bäst.",
  "/elavtal/vintersakrat": "Vintersäkrade elavtal skyddar mot pristopparna under vinterhalvåret. Så fungerar de och vad de kostar.",
  "/elavtal/black-friday": "Black Friday-erbjudanden på elavtal – jämför kampanjpriser och se vad som gäller efter kampanjperioden.",

  // Elbolag
  "/elbolag": "Jämför Sveriges elbolag: priser, avtal, villkor och kundbetyg – hitta elhandlaren som passar dig.",
  "/elbolag/jamfor": "Jämför elbolagen sida vid sida på pris, påslag, månadsavgift och villkor.",
  "/elbolag/byt": "Byt elbolag kostnadsfritt – så går bytet till, hur lång tid det tar och vad du bör kontrollera först.",
  "/elbolag/basta": "Bästa elbolagen utifrån pris, villkor och kundnöjdhet – uppdaterad lista med aktuella avtal.",
  "/elbolag/billigaste": "Billigaste elbolagen just nu – jämför påslag, månadsavgift och totalpris för din förbrukning.",

  // Elpriser och spotpris
  "/elpriser": "Aktuella elpriser per elområde och kommun – dagens och morgondagens priser per timme och kvart.",
  "/elpriser/idag": "Dagens elpris timme för timme i SE1–SE4 – se billigaste och dyraste timmarna just idag.",
  "/elpriser/imorgon": "Morgondagens elpriser publiceras varje eftermiddag – se prisbilden per timme innan dagen börjar.",
  "/elpriser/prognos": "Elprisprognos för kommande dagar och veckor baserad på väder, vattenmagasin och marknadsläge.",
  "/elpriser/nordpool": "Elpriser från elbörsen Nord Pool – så sätts priset och hur det påverkar ditt elavtal.",
  "/elpriser/jamfor": "Jämför elpriser mellan elområden och perioder – se skillnaderna i snittpris per kWh.",
  "/elpriser/rorligt": "Rörligt elpris per elområde – se hur spotpriset utvecklas månad för månad.",
  "/elpriser/fast": "Fasta elpriser just nu – aktuella nivåer per bindningstid och elområde.",
  "/elpriser/timpris": "Elpris per timme i SE1–SE4 – se dygnets prisprofil och planera förbrukningen.",
  "/elpriser/kvartspris": "Elpris per kvart sedan övergången till 15-minutersavräkning – se prisvariationen inom timmen.",
  "/elpriser/manadspris": "Månadens snittpris på el per elområde – historik och aktuell nivå.",
  "/dagens-elpriser": "Dagens elpriser per timme och elområde – uppdaterat varje dag från elbörsen.",
  "/morgondagens-elpriser": "Morgondagens elpriser per timme och elområde, publicerade varje eftermiddag.",
  "/spotpris": "Spotpriset på el timme för timme och kvart för kvart i SE1–SE4, idag och imorgon.",
  "/spotpris/idag": "Dagens spotpris på el per elområde – aktuellt pris, snitt, lägsta och högsta för dygnet.",
  "/spotpris/imorgon": "Morgondagens spotpris per elområde så snart elbörsen publicerat priserna.",
  "/spotpriser": "Spotpris per elområde SE1–SE4 med uppdaterade priser per timme och kvart.",
  "/historiska-elpriser": "Historiska elpriser per dag, månad och år för elområdena SE1–SE4 – med diagram och tabeller.",
  "/diagram-tabeller": "Diagram och tabeller över elpriser: jämför elområden, perioder och snitt-, tim- och kvartspriser.",

  // Verktyg
  "/verktyg": "Kostnadsfria verktyg för att sänka elkostnaden – hitta elområde, nätägare, anläggnings-ID och rätt elavtal.",
  "/verktyg/hitta-elavtal": "Hitta rätt elavtal utifrån adress, förbrukning och önskad avtalstyp.",
  "/verktyg/hitta-elbolag": "Hitta elbolagen som levererar till din adress och se deras aktuella avtal.",
  "/verktyg/hitta-elomrade": "Ta reda på vilket elområde (SE1–SE4) din adress tillhör – ange postnummer.",
  "/verktyg/hitta-natagare": "Hitta vilket elnätsbolag som äger nätet på din adress och se nätavgifterna.",
  "/verktyg/hitta-anlaggnings-id": "Så hittar du ditt anläggnings-ID – på elfakturan, hos nätbolaget eller via Mina sidor.",
  "/verktyg/mobilapp": "Elmarknads mobilapp med elpriser, prisnotiser och koll på ditt avtal.",
  "/hitta-elomrade": "Ta reda på vilket elområde din adress tillhör genom att ange postnummer.",
  "/hitta-natagare": "Hitta elnätsbolaget på din adress och se vad nätavgiften kostar.",
  "/hitta-elbolag": "Se vilka elbolag som levererar el till din adress och jämför deras avtal.",
  "/kalkylatorer": "Alla våra kalkylatorer samlade: elkostnad, solceller, batteri, värmepump och laddbox.",

  // Offerter och produkter
  "/offerter": "Begär kostnadsfria offerter på solceller, batterier, värmepump och laddbox från kvalitetssäkrade installatörer.",
  "/solceller": "Jämför offerter på solceller från kvalitetssäkrade installatörer – pris, återbetalningstid och grönt avdrag.",
  "/solceller/offert": "Få upp till fyra offerter på solceller – kostnadsfritt och utan förpliktelser.",
  "/solceller/solcellskalkylator": "Räkna ut vad solceller ger på ditt tak: produktion, besparing och återbetalningstid.",
  "/batterier": "Jämför offerter på hembatterier och batterilagring – pris, kapacitet och besparing.",
  "/batterier/batterikalkylator": "Räkna på hembatteri: lagringskapacitet, besparing per år och återbetalningstid.",
  "/hembatterier": "Hembatteri för villan – jämför offerter, se priser och räkna på besparingen.",
  "/varmepumpar": "Jämför offerter på värmepumpar – luft/vatten, frånluft och bergvärme från kvalitetssäkrade installatörer.",
  "/varmepumpar/varmepumpskalkylator": "Räkna ut hur mycket en värmepump sänker din uppvärmningskostnad.",
  "/laddboxar": "Jämför offerter på laddbox till elbilen – pris, installation och grönt teknik-avdrag.",
  "/laddboxar/laddboxkalkylator": "Räkna ut vad det kostar att ladda elbilen hemma jämfört med publik laddning.",
  "/laddstolpar": "Laddstolpar för företag, fastigheter och samfälligheter – jämför offerter och se bidrag.",
  "/kommersiella-solceller": "Kommersiella solcellsanläggningar för företag, fastigheter och lantbruk – jämför offerter.",
  "/kommersiella-batterier": "Kommersiell batterilagring för företag och fastigheter – jämför offerter och se intäkter från stödtjänster.",

  // Elmarknaden och övrigt
  "/elmarknaden": "Så fungerar den svenska elmarknaden: aktörer, prissättning, elområden och kopplingen till Europa.",
  "/elmarknaden/sa-fungerar-elmarknaden": "Elmarknaden steg för steg – produktion, elbörs, elnät och elhandel förklarat.",
  "/elmarknaden/historia": "Elmarknadens historia i Sverige – från monopol till avreglering och dagens elområden.",
  "/elmarknaden/avregleringen": "Avregleringen 1996 – varför den gjordes och vad den betyder för dig som elkund.",
  "/elmarknaden/framtiden": "Elmarknadens framtid: elektrifiering, ny produktion, flexibilitet och vad det gör med priserna.",
  "/elnat": "Elnätet, nätavgifter och elnätsområden i Sverige – så påverkar nätbolaget din elräkning.",
  "/elhandel": "Sveriges elhandelsbolag – jämför avtal, priser och villkor per bolag.",
  "/blogg": "Guider, nyheter och analyser om elpriser, elavtal och energi i hemmet.",
  "/nyheter": "Nyheter om elmarknaden, elpriser och energipolitik – uppdaterat löpande.",
  "/rapporter": "Månadsrapporter om elpriser och elmarknaden med statistik per elområde.",
  "/ordlista": "Ordlista med förklaringar av begrepp inom el, elavtal, elnät och energi.",
  "/kommun": "Elpriser och elavtal per kommun – se vilket elområde din kommun tillhör.",
  "/om-oss": "Om Elmarknad.se – vilka vi är, hur jämförelsen fungerar och hur vi tjänar pengar.",
  "/kontakt": "Kontakta Elmarknad.se – kundtjänst, telefon och e-post.",
  "/fragor-svar": "Vanliga frågor och svar om elavtal, elpriser, byten och våra tjänster.",
  "/bli-partner": "Bli partner till Elmarknad.se och få kvalificerade leads inom el och energi.",
};

const DEFAULT_DESCRIPTION =
  "Jämför elavtal och få offerter på solceller, batterier, värmepumpar och elbilsladdare – samlat hos Elmarknad.se.";

/** Löser beskrivningen för en sökväg: exakt träff först, annars längsta prefix. */
export function seoDescriptionForPath(path: string): string {
  let p = normalizePath(path);
  const override = getSeoOverride(p)?.description?.trim();
  if (override) return override;
  while (p.includes("/$")) {
    p = p.slice(0, p.lastIndexOf("/")) || "/";
  }
  const exact = PATH_DESCRIPTIONS[p];
  if (exact) return exact;
  if (p === "/") return DEFAULT_DESCRIPTION;




  let best = "";
  let description = DEFAULT_DESCRIPTION;
  for (const [prefix, text] of SECTION_DESCRIPTIONS) {
    if ((p === prefix || p.startsWith(`${prefix}/`)) && prefix.length > best.length) {
      best = prefix;
      description = text;
    }
  }
  return description;
}

/** Absolut sid-URL för og:url (dynamiska segment klipps bort). */
function absoluteUrl(path: string): string {
  let p = normalizePath(path);
  while (p.includes("/$")) {
    p = p.slice(0, p.lastIndexOf("/")) || "/";
  }
  return p === "/" ? `${SITE_URL}/` : `${SITE_URL}${p}`;
}

export interface SeoHeadOptions {
  /** Skriv över titeln helt (t.ex. dynamiska mallsidor). Admin-överskrivning vinner ändå. */
  title?: string;
  /** Skriv över beskrivningen (t.ex. för unika undersidor). */
  description?: string;
  /** og:type – "website" (standard) eller t.ex. "article", "product". */
  ogType?: string;
  /** Absolut URL till en sidspecifik delningsbild (1200×630). */
  image?: string;
  /** Alt-text till delningsbilden. */
  imageAlt?: string;
  /** Blockera indexering (sökresultat, formulär, tacksidor). */
  noindex?: boolean;
  /**
   * @deprecated All metadata renderas numera i ruttens head() så den finns i
   * serverns HTML. Flaggan ignoreras och tas bort när alla rutter är städade.
   */
  metaFromPage?: boolean;
}

/**
 * head()-objekt med prerenderad titel, beskrivning, canonical och Open
 * Graph-/Twitter Card-taggar för en given sökväg.
 */
export function seoHead(path: string, options: SeoHeadOptions = {}) {
  const override = getSeoOverride(normalizePath(path));
  const title = override?.title?.trim() || options.title?.trim() || seoTitleForPath(path);
  const description = buildDescription(
    override?.description?.trim() || options.description || seoDescriptionForPath(path),
  )!;
  const url = absoluteUrl(path);
  const image = override?.og_image?.trim() || options.image;
  const noindex = Boolean(override?.noindex) || Boolean(options.noindex);

  return {
    meta: [
      { title },

      { property: "og:url", content: url },
      { property: "og:type", content: options.ogType ?? "website" },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:locale", content: "sv_SE" },

      { name: "twitter:card", content: "summary_large_image" },

      ...(noindex
        ? [
            { name: "robots", content: "noindex, follow" },
            { name: "googlebot", content: "noindex, follow" },
          ]
        : []),

      { property: "og:title", content: title },
      { name: "twitter:title", content: title },
      { name: "description", content: description },
      { property: "og:description", content: description },
      { name: "twitter:description", content: description },

      ...(image
        ? [
            { property: "og:image", content: image },
            { property: "og:image:width", content: "1200" },
            { property: "og:image:height", content: "630" },
            ...(options.imageAlt
              ? [
                  { property: "og:image:alt", content: options.imageAlt },
                  { name: "twitter:image:alt", content: options.imageAlt },
                ]
              : []),
            { name: "twitter:image", content: image },
          ]
        : []),
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

/** Sidfält som kan förse head() med titel, beskrivning och delningsbild. */
export interface SeoPageSource {
  path?: string | null;
  title?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  meta_description?: string | null;
  hero_image?: string | null;
}

/**
 * head()-objekt för databasdrivna sidor (blogg, nyheter, elnät, elhandel …).
 * Titel, beskrivning och delningsbild hämtas ur den laddade sidan så att de
 * finns i serverns svar – inte först efter hydrering.
 */
export function seoHeadFromPage(
  path: string,
  page: SeoPageSource | null | undefined,
  options: SeoHeadOptions = {},
) {
  if (!page) return seoHead(path, options);

  const rawTitle = (page.seo_title || page.title || "").trim();
  const title =
    options.title?.trim() ||
    (rawTitle ? buildTitle(stripTrailingBrand(rawTitle), "page") : "");
  const description =
    options.description ||
    usableDescription(page.seo_description) ||
    usableDescription(page.meta_description) ||
    "";
  const image = options.image || page.hero_image?.trim() || "";

  return seoHead(path, {
    ...options,
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
  });
}

/**
 * Importerade beskrivningar som egentligen är mallrester från gamla sajten
 * (formulärets samtyckestext, betygsrutan, laddningsmeddelanden). De används
 * aldrig som metabeskrivning – sidans standardbeskrivning tar över.
 */
const BOILERPLATE_DESCRIPTIONS = [
  /^genom att skicka din förfrågan/i,
  /^\+?\s*\d[\d.\s]*nöjda kunder/i,
  /^datahämtning avbruten/i,
];

export function usableDescription(text: string | null | undefined): string | undefined {
  const value = text?.trim();
  if (!value) return undefined;
  return BOILERPLATE_DESCRIPTIONS.some((re) => re.test(value)) ? undefined : value;
}

/** Tar bort ett eventuellt "| Elmarknad.se"-suffix innan mallen läggs på. */
function stripTrailingBrand(value: string): string {
  return value.replace(/\s*[|｜–—-]\s*Elmarknad(\.se)?\s*$/i, "").trim();
}

